import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowRight,
  Banknote,
  BriefcaseBusiness,
  Building2,
  CheckCircle2,
  CircleAlert,
  Clock3,
  RefreshCw,
  ShieldCheck,
  Target,
  TrendingUp,
  Truck,
} from 'lucide-react';
import { CargasSection } from '@/pages/Cargas/components/CargasLayout.jsx';
import { useDashboardData } from '@/pages/Cargas/dashboard/useDashboardData.js';
import { formatCurrencyARS, formatNumber, formatPercent } from '@/pages/Cargas/dashboard/formatters.js';

const kpiIcons = {
  'guias-mes': BriefcaseBusiness,
  transito: Truck,
  'entregadas-hoy': Target,
  novedad: CircleAlert,
  'pendiente-cobro': Banknote,
  cumplimiento: ShieldCheck,
};

const statusChipClassNames = {
  positive: 'border-emerald-500/20 bg-emerald-500/12 text-emerald-700',
  neutral: 'border-sky-500/20 bg-sky-500/12 text-sky-700',
  warning: 'border-amber-500/20 bg-amber-500/12 text-amber-700',
  critical: 'border-rose-500/20 bg-rose-500/12 text-rose-700',
};

const severityChipClassNames = {
  Critico: 'border-rose-500/22 bg-rose-500/10 text-rose-700',
  Alerta: 'border-amber-500/20 bg-amber-500/10 text-amber-700',
  Seguimiento: 'border-sky-500/20 bg-sky-500/10 text-sky-700',
};

const branchToneClassNames = {
  Normal: 'border-emerald-500/18 bg-emerald-500/8 text-emerald-700',
  Demorado: 'border-amber-500/18 bg-amber-500/8 text-amber-700',
  Critico: 'border-rose-500/18 bg-rose-500/8 text-rose-700',
};

const monthlyToneClassNames = {
  positive: 'border-emerald-500/18 bg-emerald-500/10 text-emerald-700',
  neutral: 'border-sky-500/18 bg-sky-500/10 text-sky-700',
  warning: 'border-amber-500/18 bg-amber-500/10 text-amber-700',
  critical: 'border-rose-500/18 bg-rose-500/10 text-rose-700',
};

const SECTION_LABELS = {
  volumen: 'volumen de guías',
  cohorteMes: 'embudo del mes',
  enCurso: 'guías en curso',
  entregasDia: 'entregas del día',
  sla: 'entregas a tiempo',
  novedades: 'novedades',
  cartera: 'cartera de clientes',
  cobranzaHoy: 'cobranza del día',
  sinFacturar: 'guías sin facturar',
  facturacion: 'facturación mensual',
  cobranzas: 'cobranza mensual',
  facturacionMtd: 'facturación del mes',
  repartosAbiertos: 'repartos abiertos',
  fojasDemoradas: 'fojas demoradas',
  sucursales: 'estado por sucursal',
  vencidoPorSucursal: 'deuda por sucursal',
  topClientes: 'ranking de clientes',
  topChoferes: 'ranking de choferes',
  sucursal: 'datos de la sucursal',
};

const formatGeneratedAt = (value) => {
  const date = value ? new Date(value) : null;
  if (!date || Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat('es-AR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false }).format(date).replace(',', ' ·');
};

function formatKpiValue(metric) {
  if (metric.value === null || metric.value === undefined) return '—';
  if (metric.format === 'currency') return formatCurrencyARS(metric.value);
  if (metric.format === 'percent') return formatPercent(metric.value, 1);
  return formatNumber(metric.value);
}

function ExecutiveHeader({ header, generatedAt, loading, onRefresh }) {
  return (
    <header className="relative min-w-0 overflow-hidden rounded-[24px] border border-[rgba(var(--border),0.52)] bg-[linear-gradient(120deg,rgba(var(--panel),0.98)_0%,rgba(var(--panel),0.96)_52%,rgba(var(--brand),0.08)_100%)] px-4 py-3 shadow-[0_10px_24px_rgba(var(--shadow),0.08)] max-[1440px]:rounded-[18px] max-[1440px]:px-3 max-[1440px]:py-2">
      <div className="absolute -right-10 -top-10 h-24 w-24 rounded-full bg-[rgba(var(--brand),0.12)] blur-3xl max-[1440px]:h-16 max-[1440px]:w-16" />
      <div className="relative flex min-w-0 flex-col gap-2 max-[1440px]:gap-1.5 xl:flex-row xl:items-center xl:justify-between">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h1 className="truncate text-[1.28rem] font-semibold text-[rgb(var(--fg))] max-[1440px]:text-[1.12rem] 2xl:text-[1.5rem]">
              {header.title}
            </h1>
            <span className="shrink-0 rounded-full bg-[rgba(var(--brand),0.12)] px-2 py-0.5 text-[9px] font-semibold uppercase tracking-[0.16em] text-[rgb(var(--brand))] max-[1440px]:px-1.5 max-[1440px]:text-[8px]">
              Ejecutivo
            </span>
          </div>
          <p className="mt-0.5 truncate text-[12px] text-[rgba(var(--fg),0.68)] max-[1440px]:text-[11px]" title={header.subtitle}>
            {header.subtitle}
          </p>
        </div>

        <div className="flex min-w-0 flex-wrap gap-1.5 max-[1440px]:gap-1 xl:justify-end">
          <ContextChip label="Periodo" value={header.periodLabel} />
          <ContextChip label="Sucursal" value={header.branchLabel} />
          <ContextChip label="Última actualización" value={formatGeneratedAt(generatedAt)} />
          <button
            type="button"
            onClick={onRefresh}
            disabled={loading}
            className="inline-flex items-center gap-1 rounded-[14px] border border-[rgba(var(--brand),0.24)] bg-[rgba(var(--brand),0.1)] px-2.5 py-1 text-[11px] font-semibold text-[rgb(var(--brand))] disabled:opacity-60"
          >
            <RefreshCw size={12} className={loading ? 'animate-spin' : ''} aria-hidden="true" />
            {loading ? 'Actualizando…' : 'Actualizar'}
          </button>
        </div>
      </div>
    </header>
  );
}

function ContextChip({ label, value, tone = 'default' }) {
  const toneClassName =
    tone === 'brand'
      ? 'border-[rgba(var(--brand),0.24)] bg-[rgba(var(--brand),0.1)] text-[rgb(var(--brand))]'
      : 'border-[rgba(var(--border),0.48)] bg-[rgba(var(--bg),0.34)] text-[rgba(var(--fg),0.74)]';

  return (
    <div className={`min-w-0 rounded-[14px] border px-2.5 py-1 max-[1440px]:rounded-[11px] max-[1440px]:px-2 max-[1440px]:py-0.5 ${toneClassName}`}>
      <div className="truncate text-[9px] uppercase tracking-[0.14em] opacity-75 max-[1440px]:text-[8px]">{label}</div>
      <div className="mt-0.5 truncate text-[11px] font-semibold leading-none max-[1440px]:text-[10px]" title={value}>{value}</div>
    </div>
  );
}

function ExecutiveKpiCard({ metric }) {
  const Icon = kpiIcons[metric.id] || TrendingUp;
  const statusClassName = statusChipClassNames[metric.status] || statusChipClassNames.neutral;
  const isPriority = metric.emphasis === 'risk' || metric.id === 'cumplimiento';

  return (
    <article
      data-testid={`kpi-${metric.id}`}
      className={`relative min-h-[108px] min-w-0 overflow-hidden rounded-[20px] border p-3 max-[1440px]:min-h-[96px] max-[1440px]:rounded-[16px] max-[1440px]:p-2.5 ${
        isPriority
          ? 'border-[rgba(var(--brand),0.18)] bg-[linear-gradient(160deg,rgba(var(--panel),0.98),rgba(var(--brand),0.05))]'
          : 'border-[rgba(var(--border),0.5)] bg-[rgba(var(--panel),0.92)]'
      } shadow-[0_8px_22px_rgba(var(--shadow),0.06)]`}
    >
      <div className="flex min-w-0 items-start justify-between gap-3 max-[1440px]:gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex min-w-0 items-center gap-2 max-[1440px]:gap-1.5">
            <div className="shrink-0 rounded-xl border border-[rgba(var(--border),0.48)] bg-[rgba(var(--bg),0.34)] p-1.5 text-[rgb(var(--brand))] max-[1440px]:rounded-lg max-[1440px]:p-1">
              <Icon size={14} className="max-[1440px]:h-3 max-[1440px]:w-3" />
            </div>
            <p className="truncate text-[10px] font-semibold uppercase tracking-[0.16em] text-[rgba(var(--muted),0.84)] max-[1440px]:text-[9px]" title={metric.label}>
              {metric.label}
            </p>
          </div>
          <div className="mt-2 flex min-w-0 items-end justify-between gap-3 max-[1440px]:mt-1.5">
            <div className="min-w-0">
              <div className="truncate text-[1.34rem] font-semibold leading-none text-[rgb(var(--fg))] max-[1440px]:text-[1.18rem] 2xl:text-[1.5rem]">
                {formatKpiValue(metric)}
              </div>
              <div className="mt-2 flex min-w-0 flex-wrap items-center gap-1.5 max-[1440px]:mt-1.5 max-[1440px]:gap-1">
                <span className={`shrink-0 rounded-full border px-2 py-0.5 text-[10px] font-semibold max-[1440px]:px-1.5 max-[1440px]:text-[9px] ${statusClassName}`}>
                  {metric.delta}
                </span>
                {metric.note ? (
                  <span className="min-w-0 truncate text-[11px] text-[rgba(var(--fg),0.66)] max-[1440px]:text-[10px]" title={metric.note}>
                    {metric.note}
                  </span>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </div>
    </article>
  );
}

function ExecutiveHealthPanel({ overview }) {
  const breakdown = overview.healthBreakdown || [];
  return (
    <div role="region" aria-label="Estado general" className="h-full min-w-0">
      <CargasSection
        title="Estado general"
        description="Puntaje 0-100 ponderado: entregas a tiempo 40%, flujo 20%, cartera 25%, novedades 15%."
        compact
        density="tight"
        className="h-full min-w-0 max-[1440px]:h-auto"
        headerClassName="max-[1440px]:px-3 max-[1440px]:py-1"
        bodyClassName="space-y-3 max-[1440px]:p-2"
      >
        <div className="grid min-w-0 gap-3 max-[900px]:grid-cols-1 sm:grid-cols-[88px_minmax(0,1fr)]">
          <div
            className="flex h-[88px] w-[88px] items-center justify-center rounded-full border-4"
            style={{ borderColor: healthColor(overview.healthScore) }}
            title="Promedio ponderado de los componentes con datos disponibles"
          >
            <div className="text-center">
              <div className="text-[26px] font-semibold leading-none text-[rgb(var(--fg))]">{overview.healthScore ?? '—'}</div>
              <div className="mt-1 text-[9px] font-semibold uppercase tracking-[0.14em] text-[rgba(var(--muted),0.82)]">Salud</div>
            </div>
          </div>
          <div className="min-w-0 space-y-2">
            <div className="min-w-0 rounded-[16px] border border-[rgba(var(--border),0.44)] bg-[rgba(var(--bg),0.3)] px-3 py-2">
              <div className="text-[10px] uppercase tracking-[0.14em] text-[rgba(var(--muted),0.8)]">Lectura gerencial</div>
              <div className="mt-1 text-[13px] font-semibold text-[rgb(var(--fg))]">{overview.headline}</div>
              <div className="mt-1 line-clamp-2 text-[11px] leading-snug text-[rgba(var(--fg),0.64)]" title={overview.summary}>{overview.summary}</div>
            </div>
            {breakdown.length ? (
              <div className="grid min-w-0 gap-x-3 gap-y-1.5 sm:grid-cols-2">
                {breakdown.map(item => (
                  <div key={item.id} className="min-w-0">
                    <div className="flex items-center justify-between gap-2 text-[10px]">
                      <span className="truncate text-[rgba(var(--fg),0.7)]">{item.label}</span>
                      <span className="font-semibold text-[rgb(var(--fg))]">{item.score}</span>
                    </div>
                    <div className="mt-0.5 h-1.5 overflow-hidden rounded-full bg-[rgba(var(--border),0.35)]">
                      <div className="h-full rounded-full" style={{ width: `${Math.max(item.score, 2)}%`, background: healthColor(item.score) }} />
                    </div>
                  </div>
                ))}
              </div>
            ) : null}
            <div className="grid min-w-0 gap-2 sm:grid-cols-3">
              {overview.pillars.map(item => (
                <div key={item.label} className="min-w-0 rounded-[14px] border border-[rgba(var(--border),0.42)] bg-[rgba(var(--bg),0.26)] px-2.5 py-1.5">
                  <div className="truncate text-[9px] uppercase tracking-[0.14em] text-[rgba(var(--muted),0.82)]">{item.label}</div>
                  <div className="mt-0.5 truncate text-[14px] font-semibold text-[rgb(var(--fg))]" title={item.value}>{item.value}</div>
                  <div className="line-clamp-1 text-[10px] text-[rgba(var(--fg),0.62)]" title={item.note}>{item.note}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </CargasSection>
    </div>
  );
}

function healthColor(score) {
  if (score === null || score === undefined) return 'rgba(var(--muted),0.5)';
  if (score >= 85) return 'rgb(var(--success))';
  if (score >= 70) return 'rgb(var(--warning))';
  return 'rgb(var(--danger))';
}

function RiskPanel({ alerts, href = '/gerencial/alertas' }) {
  const visibleAlerts = alerts.slice(0, 4);

  return (
    <CargasSection
      title="Riesgos que requieren accion"
      description="Ordenados por severidad, calculados sobre la operación real."
      compact
      density="tight"
      className="h-full min-w-0"
      headerClassName="max-[1440px]:px-3"
      actions={
        <Link
          to={href}
          className="inline-flex items-center gap-1 rounded-full border border-[rgba(var(--border),0.46)] bg-[rgba(var(--bg),0.34)] px-2 py-1 text-[10px] font-semibold text-[rgb(var(--brand))]"
        >
          Ver todas
          <ArrowRight size={12} />
        </Link>
      }
      bodyClassName="space-y-2"
    >
      {visibleAlerts.length === 0 ? (
        <div className="flex items-center gap-2 rounded-[16px] border border-emerald-500/20 bg-emerald-500/8 px-3 py-3 text-[12px] font-medium text-[rgb(var(--fg))]">
          <CheckCircle2 size={16} className="text-emerald-600" aria-hidden="true" />
          Sin riesgos activos. La operación está bajo control.
        </div>
      ) : null}
      {visibleAlerts.map(alert => (
        <div
          key={alert.id}
          className={`min-w-0 rounded-[16px] border px-3 py-2.5 max-[1440px]:rounded-[13px] max-[1440px]:px-2.5 max-[1440px]:py-2 ${severityChipClassNames[alert.severity] || severityChipClassNames.Seguimiento}`}
        >
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="rounded-full bg-white/70 px-2 py-0.5 text-[9px] font-semibold uppercase tracking-[0.14em] text-[rgb(var(--fg))]">
                  {alert.severity}
                </span>
                <span className="text-[11px] font-semibold">{formatNumber(alert.count)}</span>
              </div>
              <div className="mt-1 text-[12px] font-semibold text-[rgb(var(--fg))]">{alert.text}</div>
              <div className="mt-1 text-[11px] text-[rgba(var(--fg),0.7)]">{alert.impact}</div>
            </div>
            <div className="rounded-xl bg-white/70 p-1.5">
              {alert.severity === 'Critico' ? <AlertTriangle size={14} /> : <Clock3 size={14} />}
            </div>
          </div>
          <div className="mt-2">
            <Link to={alert.href || href} className="inline-flex items-center gap-1 text-[10px] font-semibold text-[rgb(var(--brand))]">
              Ver detalle
              <ArrowRight size={11} />
            </Link>
          </div>
        </div>
      ))}
    </CargasSection>
  );
}

function FinancialSnapshot({ financialSummary }) {
  const topAmount =
    financialSummary.monthlyTrend.reduce(
      (max, item) => Math.max(max, item.billing || 0, item.collection || 0),
      1,
    ) || 1;

  return (
    <CargasSection
      title="Caja y cobranza"
      description="Pendiente, vencido, rendiciones y tendencia mensual."
      compact
      density="tight"
      className="h-full min-w-0"
      headerClassName="max-[1440px]:px-3"
      bodyClassName="space-y-3"
    >
      <div className="grid min-w-0 gap-2 sm:grid-cols-2 2xl:grid-cols-4">
        {financialSummary.snapshot.map(item => (
          <div key={item.id} className="min-w-0 rounded-[16px] border border-[rgba(var(--border),0.42)] bg-[rgba(var(--bg),0.28)] px-3 py-2.5 max-[1440px]:rounded-[13px] max-[1440px]:px-2.5 max-[1440px]:py-2">
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0 truncate text-[9px] uppercase tracking-[0.14em] text-[rgba(var(--muted),0.82)]">{item.label}</div>
              <span className={`rounded-full border px-2 py-0.5 text-[9px] font-semibold ${statusChipClassNames[item.status] || statusChipClassNames.neutral}`}>
                {item.badge}
              </span>
            </div>
            <div className="mt-1.5 truncate text-[18px] font-semibold text-[rgb(var(--fg))] max-[1440px]:text-[16px]" title={item.value === null ? 'Dato no disponible' : formatCurrencyARS(item.value)}>{item.value === null ? '—' : formatCurrencyARS(item.value)}</div>
            <div className="mt-1 line-clamp-1 text-[10px] text-[rgba(var(--fg),0.64)]" title={item.note}>{item.note}</div>
          </div>
        ))}
      </div>

      <div className="rounded-[18px] border border-[rgba(var(--border),0.42)] bg-[rgba(var(--bg),0.22)] p-3">
        <div className="mb-3 flex items-center justify-between gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.14em] text-[rgba(var(--muted),0.82)]">Tendencia mensual</div>
            <div className="mt-1 text-[11px] text-[rgba(var(--fg),0.64)]">Facturacion vs cobranza real</div>
          </div>
          <div className="flex flex-wrap gap-1.5 text-[9px] font-semibold">
            <span className="inline-flex items-center gap-1 rounded-full bg-[rgba(var(--brand),0.12)] px-2 py-1 text-[rgb(var(--brand))]">
              <span className="h-2 w-2 rounded-full bg-[rgb(var(--brand))]" />
              Facturacion
            </span>
            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/12 px-2 py-1 text-emerald-700">
              <span className="h-2 w-2 rounded-full bg-emerald-600" />
              Cobranza
            </span>
          </div>
        </div>

        <div className="grid min-w-0 gap-2 md:grid-cols-3">
          {financialSummary.monthlyTrend.map(item => {
            const billingWidth = `${Math.max(((item.billing || 0) / topAmount) * 100, 2)}%`;
            const collectionWidth = `${Math.max(((item.collection || 0) / topAmount) * 100, 2)}%`;
            return (
              <div key={item.month} className="rounded-[16px] border border-[rgba(var(--border),0.38)] bg-[rgba(var(--panel),0.78)] px-3 py-2.5">
                <div className="flex items-center justify-between gap-2">
                  <div className="text-[12px] font-semibold text-[rgb(var(--fg))]">{item.month}</div>
                  <div className="text-[10px] text-[rgba(var(--fg),0.62)]">{item.badge}</div>
                </div>
                <div className="mt-2 space-y-2">
                  <TrendBar label="Fact." value={formatCurrencyARS(item.billing)} width={billingWidth} tone="brand" />
                  <TrendBar label="Cobr." value={formatCurrencyARS(item.collection)} width={collectionWidth} tone="emerald" />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </CargasSection>
  );
}

function TrendBar({ label, value, width, tone }) {
  const toneClassName =
    tone === 'emerald'
      ? 'bg-[linear-gradient(90deg,rgba(16,185,129,0.34),rgba(5,150,105,0.88))]'
      : 'bg-[linear-gradient(90deg,rgba(var(--brand),0.34),rgba(var(--brand),0.96))]';

  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between gap-2 text-[10px]">
        <span className="text-[rgba(var(--fg),0.66)]">{label}</span>
        <span className="font-semibold text-[rgb(var(--fg))]">{value}</span>
      </div>
      <div className="h-2 overflow-hidden rounded-full bg-[rgba(var(--bg),0.56)]">
        <div className={`h-full rounded-full ${toneClassName}`} style={{ width }} />
      </div>
    </div>
  );
}

function OperationalFunnel({ items }) {
  const maxValue = Math.max(...items.map(item => item.value), 1);

  return (
    <div className="space-y-2">
      {items.map((item, index) => {
        const width = `${Math.max((item.value / maxValue) * 100, 8)}%`;

        return (
          <div
            key={item.stage}
            className={`rounded-[16px] border px-3 py-2 ${
              item.isBottleneck ? 'border-amber-500/22 bg-amber-500/8' : 'border-[rgba(var(--border),0.4)] bg-[rgba(var(--bg),0.22)]'
            }`}
          >
            <div className="grid grid-cols-[minmax(0,132px)_minmax(0,1fr)_76px] items-center gap-2">
              <div className="min-w-0">
                <div className="flex items-center gap-1.5">
                  <div className="truncate text-[12px] font-semibold text-[rgb(var(--fg))]">{item.stage}</div>
                  <span className="rounded-full bg-[rgba(var(--bg),0.56)] px-1.5 py-0.5 text-[8px] font-semibold uppercase tracking-[0.12em] text-[rgba(var(--muted),0.82)]">
                    E{index + 1}
                  </span>
                </div>
                <div className="mt-1 line-clamp-1 text-[10px] text-[rgba(var(--fg),0.62)]" title={item.note}>
                  {item.note}
                </div>
              </div>
              <div className="space-y-1">
                <div className="h-2 overflow-hidden rounded-full bg-[rgba(var(--bg),0.6)]">
                  <div className="h-full rounded-full" style={{ width, background: item.tone }} />
                </div>
                <div className="flex items-center justify-between gap-2 text-[10px] text-[rgba(var(--fg),0.66)]">
                  <span>{formatPercent(item.share)}</span>
                  {item.isBottleneck ? <span className="font-semibold text-amber-700">accion requerida</span> : null}
                </div>
              </div>
              <div className="text-right">
                <div className="text-[14px] font-semibold tracking-[-0.03em] text-[rgb(var(--fg))]">{formatNumber(item.value)}</div>
                {item.href ? (
                  <Link to={item.href} className="inline-flex items-center gap-1 text-[10px] font-semibold text-[rgb(var(--brand))]">
                    Ver
                    <ArrowRight size={11} />
                  </Link>
                ) : null}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function BranchHealthTable({ branches }) {
  if (!branches.length) {
    return <p className="py-4 text-center text-[11px] text-[rgba(var(--fg),0.6)]">Sin guías en los últimos 90 días para comparar sucursales.</p>;
  }
  return (
    <div className="grid gap-2 md:grid-cols-2 2xl:grid-cols-3">
      {branches.map(branch => (
        <article key={branch.name} className="rounded-[18px] border border-[rgba(var(--border),0.45)] bg-[rgba(var(--bg),0.24)] p-3">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <div className="rounded-xl bg-[rgba(var(--brand),0.12)] p-1.5 text-[rgb(var(--brand))]">
                  <Building2 size={13} />
                </div>
                <div className="truncate text-[13px] font-semibold text-[rgb(var(--fg))]" title={branch.name}>
                  {branch.name}
                </div>
              </div>
              <div className="mt-1 text-[10px] text-[rgba(var(--fg),0.62)]">{branch.subtext}</div>
            </div>
            <span className={`rounded-full border px-2 py-0.5 text-[9px] font-semibold ${branchToneClassNames[branch.status] || branchToneClassNames.Normal}`}>
              {branch.status}
            </span>
          </div>

          <div className="mt-3 grid grid-cols-3 gap-2">
            <BranchMetric label="Guias" value={formatNumber(branch.guides)} />
            <BranchMetric label="Pend." value={formatNumber(branch.pending)} />
            <BranchMetric label="SLA" value={branch.sla} />
          </div>

          <div className="mt-3 flex flex-wrap gap-1">
            {branch.riskChips.slice(0, 3).map(chip => (
              <span
                key={`${branch.name}-${chip}`}
                className="rounded-full border border-[rgba(var(--border),0.46)] bg-[rgba(var(--panel),0.82)] px-2 py-0.5 text-[9px] font-medium text-[rgba(var(--fg),0.74)]"
              >
                {chip}
              </span>
            ))}
          </div>

          <div className="mt-3 flex items-center justify-between gap-2">
            <div className="line-clamp-2 text-[10px] text-[rgba(var(--fg),0.64)]">{branch.note}</div>
            <Link to={branch.href || '#'} className="inline-flex items-center gap-1 text-[10px] font-semibold text-[rgb(var(--brand))]">
              Ver detalle
              <ArrowRight size={11} />
            </Link>
          </div>
        </article>
      ))}
    </div>
  );
}

function BranchMetric({ label, value }) {
  return (
    <div className="rounded-[14px] border border-[rgba(var(--border),0.42)] bg-[rgba(var(--panel),0.74)] px-2 py-1.5">
      <div className="text-[8px] uppercase tracking-[0.14em] text-[rgba(var(--muted),0.82)]">{label}</div>
      <div className="mt-1 text-[12px] font-semibold text-[rgb(var(--fg))]">{value}</div>
    </div>
  );
}

function ProductivityRanking({ tabs }) {
  const [activeId, setActiveId] = useState(tabs[0]?.id);
  const activeTab = tabs.find(tab => tab.id === activeId) || tabs[0];
  if (!activeTab) return null;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-1" role="tablist" aria-label="Rankings">
        {tabs.map(tab => (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={tab.id === activeTab.id}
            onClick={() => setActiveId(tab.id)}
            className={`rounded-full border px-2.5 py-1 text-[10px] font-semibold transition ${
              tab.id === activeTab.id
                ? 'border-[rgba(var(--brand),0.22)] bg-[rgba(var(--brand),0.12)] text-[rgb(var(--brand))]'
                : 'border-[rgba(var(--border),0.46)] bg-[rgba(var(--bg),0.34)] text-[rgba(var(--fg),0.68)] hover:text-[rgb(var(--fg))]'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="space-y-2" role="tabpanel">
        {activeTab.items.length === 0 ? (
          <p className="py-4 text-center text-[11px] text-[rgba(var(--fg),0.6)]">Sin datos para este ranking en el mes.</p>
        ) : null}
        {activeTab.items.slice(0, 5).map((item, index) => (
          <div key={item.name} className="grid grid-cols-[28px_minmax(0,1fr)_84px] items-center gap-2 rounded-[16px] border border-[rgba(var(--border),0.42)] bg-[rgba(var(--bg),0.22)] px-3 py-2">
            <div className="flex h-7 w-7 items-center justify-center rounded-xl bg-[rgba(var(--brand),0.12)] text-[10px] font-semibold text-[rgb(var(--brand))]">
              {index + 1}
            </div>
            <div className="min-w-0">
              <div className="truncate text-[12px] font-semibold text-[rgb(var(--fg))]">{item.name}</div>
              <div className="truncate text-[10px] text-[rgba(var(--fg),0.62)]">{item.meta}</div>
            </div>
            <div className="text-right text-[11px] font-semibold text-[rgb(var(--fg))]">{item.value}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function MonthlyComparison({ items }) {
  return (
    <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-4">
      {items.map(item => (
        <article key={item.label} className="rounded-[16px] border border-[rgba(var(--border),0.42)] bg-[rgba(var(--bg),0.22)] px-3 py-2.5">
          <div className="text-[9px] uppercase tracking-[0.14em] text-[rgba(var(--muted),0.82)]">{item.label}</div>
          <div className="mt-1.5 flex items-center justify-between gap-2">
            <span className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold ${monthlyToneClassNames[item.tone] || monthlyToneClassNames.positive}`}>
              {item.value}
            </span>
            <span className="text-[11px] font-semibold text-[rgb(var(--fg))]">{item.current}</span>
          </div>
          <div className="mt-1 text-[10px] text-[rgba(var(--fg),0.62)]">Anterior: {item.previous}</div>
        </article>
      ))}
    </div>
  );
}

function ErrorBanner({ message, hasData, onRetry }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 rounded-2xl border border-rose-500/25 bg-rose-500/10 px-4 py-3 text-sm text-rose-700">
      <span className="inline-flex items-center gap-2">
        <AlertTriangle size={16} aria-hidden="true" />
        {message}
        {hasData ? ' Se muestran los últimos datos obtenidos.' : ''}
      </span>
      <button type="button" onClick={onRetry} className="rounded-lg border border-rose-500/30 px-3 py-1.5 text-xs font-semibold">
        Reintentar
      </button>
    </div>
  );
}

export default function DashboardGerencial() {
  const { data, loading, error, reload } = useDashboardData();
  const shellClassName = 'mx-auto flex h-full min-h-0 w-full max-w-[1680px] flex-col overflow-x-hidden overflow-y-auto px-2 py-2 text-[rgb(var(--fg))] max-[1440px]:px-1.5 max-[1440px]:py-1.5 sm:px-3 lg:px-4 2xl:px-5';

  if (!data) {
    return (
      <div className={shellClassName}>
        {error ? <ErrorBanner message={error} onRetry={reload} /> : (
          <div className="grid animate-pulse gap-2.5" aria-busy="true">
            <div className="h-16 rounded-[24px] bg-[rgba(var(--muted),0.14)]" />
            <div className="grid gap-2 sm:grid-cols-3">
              {Array.from({ length: 6 }, (_, index) => <div key={index} className="h-24 rounded-[20px] bg-[rgba(var(--muted),0.12)]" />)}
            </div>
            <div className="h-64 rounded-[20px] bg-[rgba(var(--muted),0.1)]" />
          </div>
        )}
      </div>
    );
  }

  const unavailable = (data.unavailable || []).map(key => SECTION_LABELS[key] || key);

  return (
    <div className={shellClassName}>
      <div className="flex min-h-0 min-w-0 flex-col gap-2.5 max-[1440px]:gap-2">
        <ExecutiveHeader header={data.executiveHeader} generatedAt={data.generatedAt} loading={loading} onRefresh={reload} />

        {error ? <ErrorBanner message={error} hasData onRetry={reload} /> : null}

        {unavailable.length ? (
          <div className="flex items-center gap-2 rounded-2xl border border-amber-500/25 bg-amber-500/10 px-4 py-2 text-[12px] text-[rgb(var(--fg))]">
            <AlertTriangle size={15} className="text-amber-600" aria-hidden="true" />
            {`No se pudieron calcular: ${unavailable.join(', ')}. El resto del tablero está actualizado.`}
          </div>
        ) : null}

        <section className="grid min-w-0 gap-2.5 max-[1440px]:gap-2 2xl:grid-cols-[minmax(0,1.4fr)_minmax(360px,0.84fr)]">
          <div className="grid min-w-0 gap-2 sm:grid-cols-2 xl:grid-cols-3 max-[1440px]:gap-1.5">
            {data.executiveKpis.map(metric => (
              <ExecutiveKpiCard key={metric.id} metric={metric} />
            ))}
          </div>

          <ExecutiveHealthPanel overview={data.executiveOverview} />
        </section>

        <section className="grid min-w-0 gap-2.5 max-[1440px]:gap-2 xl:grid-cols-[minmax(0,0.92fr)_minmax(0,1.08fr)] 2xl:grid-cols-[minmax(0,0.84fr)_minmax(0,1.12fr)_minmax(0,1fr)]">
          <RiskPanel alerts={data.criticalAlerts} />
          <div className="min-w-0 2xl:col-span-2">
            <FinancialSnapshot financialSummary={data.financialSummary} />
          </div>
        </section>

        <section className="grid min-w-0 gap-2.5 max-[1440px]:gap-2 xl:grid-cols-[minmax(0,0.88fr)_minmax(0,1.12fr)] 2xl:grid-cols-[minmax(0,0.8fr)_minmax(0,1.12fr)_minmax(0,0.84fr)]">
          <CargasSection
            title="Embudo operativo"
            description="Emitidas, deposito, transito, reparto, entregadas y novedades."
            compact
            density="tight"
            className="h-full"
          >
            <OperationalFunnel items={data.operationalFunnel} />
          </CargasSection>

          <CargasSection
            title="Estado por sucursal"
            description="Se resaltan solo desvios, pendientes y SLA con riesgo."
            compact
            density="tight"
            className="h-full"
          >
            <BranchHealthTable branches={data.branchStatus} />
          </CargasSection>

          <CargasSection
            title="Ranking de productividad"
            description="Top corto para volumen y foco comercial."
            compact
            density="tight"
            className="h-full xl:col-span-2 2xl:col-span-1"
          >
            <ProductivityRanking tabs={data.productivityRanking.tabs} />
          </CargasSection>
        </section>

        <CargasSection
          title="Comparativo mensual"
          description="Como viene el mes frente al anterior en volumen, servicio y riesgo financiero."
          compact
          density="tight"
        >
          <MonthlyComparison items={data.monthlyComparison} />
        </CargasSection>

      </div>
    </div>
  );
}
